import type { FileUIPart, ModelMessage, ToolUIPart } from 'ai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation'
import {
  Message,
  MessageAttachment,
  MessageAttachments,
  MessageContent,
  MessageResponse,
} from '@/components/ai-elements/message'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { Suggestion, Suggestions } from '@/components/ai-elements/suggestion'
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from '@/components/ai-elements/tool'
import { TurnSnapshotAction } from '@/components/turn-snapshot-action'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { GlowingDot } from '@/components/ui/glowing-dot'
import { loadChatTurnsAsync, type Turn } from '@/lib/chats'
import {
  type ConnectorInfo,
  type ConnectorPrompt,
  getSuggestedPrompts,
} from '@/lib/connector-prompts'
import { usePlatform } from '@/lib/platform'
import { restoreFromDeltaChainExcel } from '@/lib/snapshot/delta-restore-excel'
import { calculateSnapshotDiff } from '@/lib/snapshot/diff'
import { useDiffHighlighter } from '@/lib/snapshot/highlight'
import {
  getAfterSnapshotIdForTurn,
  getDeltaChain,
  getSnapshot,
  getSnapshotIdForTurn,
  type Snapshot,
  useSnapshot,
} from '@/lib/snapshot/snapshot'
import { toModelMessage } from '@/lib/utils/message-helpers'

/**
 * Cross-fading suggestions: shows static prompts immediately, then smoothly
 * transitions to AI-generated sheet suggestions when they arrive.
 */
function SuggestionsCrossfade({
  connectors,
  sheetSuggestions,
  onSuggestionClick,
}: {
  connectors?: ConnectorInfo[]
  sheetSuggestions?: ConnectorPrompt[]
  onSuggestionClick: (prompt: string) => void
}) {
  const hasAI = sheetSuggestions && sheetSuggestions.length > 0
  const staticPrompts = useMemo(
    () => getSuggestedPrompts(connectors ?? []),
    [connectors]
  )

  // Track which prompts are currently rendered (for cross-fade)
  const [visiblePrompts, setVisiblePrompts] = useState(staticPrompts)
  const [fading, setFading] = useState(false)
  const sourceRef = useRef<'static' | 'ai'>('static')

  useEffect(() => {
    if (hasAI && sourceRef.current === 'static') {
      // AI suggestions arrived — fade out, swap, fade in
      setFading(true)
      const timer = setTimeout(() => {
        sourceRef.current = 'ai'
        setVisiblePrompts(sheetSuggestions)
        setFading(false)
      }, 200) // matches the CSS transition duration
      return () => clearTimeout(timer)
    }
  }, [hasAI, sheetSuggestions])

  return (
    <Suggestions
      className="w-full flex-wrap justify-center gap-2"
      style={{
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      {visiblePrompts.map((prompt) => (
        <Suggestion
          key={prompt.text}
          suggestion={prompt.text}
          onClick={onSuggestionClick}
          className="whitespace-normal text-left h-auto py-1.5 animate-fade-in-up"
        >
          {prompt.label ?? prompt.text}
        </Suggestion>
      ))}
    </Suggestions>
  )
}

/**
 * Find the turnId for a given turn index by matching against backend turns.
 * Returns null if no matching turn found (e.g., for streaming messages).
 */
function findTurnIdForMessageIndex(
  turns: Turn[],
  turnIndex: number
): string | null {
  if (turnIndex < turns.length) {
    return turns[turnIndex].id
  }
  return null
}

interface ChatViewProps {
  chatId: string
  messages: ModelMessage[]
  isActive: boolean
  loading: boolean
  /** True if chat was created locally but not yet persisted to backend */
  isNew?: boolean
  /** True if chat should not load/persist turns (session-only) */
  isIncognito?: boolean
  /** Active connectors for showing contextual suggestions */
  connectors?: ConnectorInfo[]
  /** AI-generated suggestions based on sheet content (overrides static suggestions when available) */
  sheetSuggestions?: ConnectorPrompt[]
  /** Called when user clicks a suggestion prompt */
  onSuggestionClick?: (prompt: string) => void
  /**
   * Called when messages are loaded from backend.
   * Only used for initial load when messages array is empty.
   */
  onMessagesLoaded?: (
    chatId: string,
    messages: ModelMessage[],
    title?: string
  ) => void
  /**
   * Called when turn metadata is loaded (for snapshots).
   * This is separate from message loading.
   */
  onTurnMetadataLoaded?: (chatId: string, turns: Turn[]) => void
}

export default function ChatTabContent({
  chatId,
  messages,
  isActive,
  loading,
  isNew,
  isIncognito,
  connectors,
  sheetSuggestions,
  onSuggestionClick,
  onMessagesLoaded,
  onTurnMetadataLoaded,
}: ChatViewProps) {
  const { restoreWorkbookSnapshot } = useSnapshot()
  const diffHighlighter = useDiffHighlighter()
  const platform = usePlatform()

  const attemptedInitialLoadChatIdRef = useRef<string | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [comparingTurnId, setComparingTurnId] = useState<string | null>(null)
  const [revertDialogOpen, setRevertDialogOpen] = useState(false)
  const [revertToAfter, setRevertToAfter] = useState(false)
  const [pendingRevertTurnId, setPendingRevertTurnId] = useState<string | null>(
    null
  )
  const [isComparingLoading, setIsComparingLoading] = useState(false)
  const [isReverting, setIsReverting] = useState(false)
  const [compareError, setCompareError] = useState<string | null>(null)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [currentSnapshotId, setCurrentSnapshotId] = useState<string | null>(
    null
  )
  const previousLoadingRef = useRef<boolean>(false)

  // Cache for snapshot availability (async lookups done on turns change)
  const [snapshotInfo, setSnapshotInfo] = useState<
    Record<string, { hasSnapshot: boolean; hasAfterSnapshot: boolean }>
  >({})

  // Load snapshot availability info when turns change
  const loadSnapshotInfo = useCallback(async () => {
    const info: Record<
      string,
      { hasSnapshot: boolean; hasAfterSnapshot: boolean }
    > = {}

    for (const turn of turns) {
      const turnId = turn.id
      if (!turnId) continue

      const snapshotId = await getSnapshotIdForTurn(turnId, chatId)
      const hasSnapshot =
        snapshotId !== null && (await getSnapshot(snapshotId)) !== null

      let hasAfterSnapshot = false
      if (hasSnapshot) {
        const afterSnapshotId = await getAfterSnapshotIdForTurn(turnId, chatId)
        hasAfterSnapshot =
          afterSnapshotId !== null &&
          (await getSnapshot(afterSnapshotId)) !== null
      }

      info[turnId] = { hasSnapshot, hasAfterSnapshot }
    }

    setSnapshotInfo(info)
  }, [turns, chatId])

  useEffect(() => {
    loadSnapshotInfo()
  }, [loadSnapshotInfo])

  // Sync turn metadata when loading completes (for snapshots only, non-destructive)
  useEffect(() => {
    if (isIncognito) {
      previousLoadingRef.current = loading
      return
    }
    // When loading transitions from true to false, sync turn metadata (NOT messages)
    if (previousLoadingRef.current && !loading && isActive) {
      const syncTurnMetadata = async () => {
        try {
          const loadedData = await loadChatTurnsAsync(chatId)
          if (loadedData) {
            // Only update turns for metadata (snapshots) - NOT messages
            setTurns(loadedData.turns)
            onTurnMetadataLoaded?.(chatId, loadedData.turns)
          }
        } catch (error) {
          console.error('Failed to sync turn metadata', error)
        }
      }
      // Small delay to ensure backend has persisted the turn
      const timeoutId = setTimeout(() => void syncTurnMetadata(), 500)
      previousLoadingRef.current = loading
      return () => clearTimeout(timeoutId)
    }
    previousLoadingRef.current = loading
  }, [loading, isActive, chatId, isIncognito, onTurnMetadataLoaded])

  useEffect(() => {
    // Skip fetching for new chats that don't exist in backend yet
    if (isNew || isIncognito) {
      return
    }
    // Only load when tab becomes active
    if (!isActive || isLoadingMessages) {
      return
    }
    // Prevent retry loops - only attempt once per chatId
    if (attemptedInitialLoadChatIdRef.current === chatId) {
      return
    }
    attemptedInitialLoadChatIdRef.current = chatId

    async function loadTurns() {
      setIsLoadingMessages(true)
      try {
        const loadedData = await loadChatTurnsAsync(chatId)
        if (loadedData) {
          // Always set turns for metadata (snapshots)
          setTurns(loadedData.turns)

          // Only populate messages if local state is empty
          // This prevents overwriting optimistic updates
          if (messages.length === 0) {
            const allMessages: ModelMessage[] = loadedData.turns.flatMap(
              (turn) => turn.messages.map(toModelMessage)
            )
            onMessagesLoaded?.(
              chatId,
              allMessages,
              loadedData.chat.title || undefined
            )
          }
        }
      } catch (error) {
        console.error('Failed to load chat turns', error)
      } finally {
        setIsLoadingMessages(false)
      }
    }

    loadTurns()
  }, [
    chatId,
    isActive,
    isNew,
    isIncognito,
    messages.length,
    isLoadingMessages,
    onMessagesLoaded,
  ])

  const handleCompareToggle = async (turnId: string) => {
    // If already comparing this turn, turn off
    if (comparingTurnId === turnId) {
      setIsComparingLoading(true)
      try {
        await diffHighlighter.clearHighlights()
        setComparingTurnId(null)
        setCompareError(null)
      } catch (error) {
        console.error('Failed to clear highlights', error)
        setCompareError('Failed to clear highlights')
      } finally {
        setIsComparingLoading(false)
      }
      return
    }

    setIsComparingLoading(true)
    setCompareError(null)

    try {
      // If comparing a different turn, clear first
      if (comparingTurnId !== null) {
        await diffHighlighter.clearHighlights()
      }

      // Get the "after" snapshot for this turn (current state)
      const afterSnapshotId = await getAfterSnapshotIdForTurn(turnId, chatId)
      if (!afterSnapshotId) {
        const errorMsg = 'After snapshot not available for this turn'
        console.warn(`No after snapshot found for turn ${turnId}`)
        setCompareError(errorMsg)
        return
      }

      const afterSnapshot = await getSnapshot(afterSnapshotId)
      if (!afterSnapshot) {
        const errorMsg = 'After snapshot expired or not found'
        console.warn(`After snapshot ${afterSnapshotId} not found or expired`)
        setCompareError(errorMsg)
        return
      }

      // Find the previous turn to get its "after" snapshot, or use this turn's "before" snapshot
      const currentTurnIndex = turns.findIndex((turn) => turn.id === turnId)

      let beforeSnapshot: Snapshot | null = null

      if (currentTurnIndex > 0) {
        // Try to get the previous turn's "after" snapshot
        const previousTurn = turns[currentTurnIndex - 1]
        if (previousTurn.id) {
          const previousAfterSnapshotId = await getAfterSnapshotIdForTurn(
            previousTurn.id,
            chatId
          )
          if (previousAfterSnapshotId) {
            beforeSnapshot = await getSnapshot(previousAfterSnapshotId)
          }
        }
      }

      // If no previous turn's "after" snapshot, use this turn's "before" snapshot
      if (!beforeSnapshot) {
        const beforeSnapshotId = await getSnapshotIdForTurn(turnId, chatId)
        if (beforeSnapshotId) {
          beforeSnapshot = await getSnapshot(beforeSnapshotId)
        }
      }

      if (!beforeSnapshot) {
        const errorMsg = 'Previous snapshot not available for comparison'
        console.warn(`No previous snapshot found for turn ${turnId}`)
        setCompareError(errorMsg)
        return
      }

      // Compare the two snapshots
      const diff = calculateSnapshotDiff(beforeSnapshot, afterSnapshot)

      // Warn if sheets were deleted
      if (diff.deletedSheets.length > 0) {
        const sheetNames = diff.deletedSheets.join(', ')
        const warningMsg = `⚠️ Warning: Sheet${diff.deletedSheets.length > 1 ? 's' : ''} "${sheetNames}" ${diff.deletedSheets.length > 1 ? 'were' : 'was'} deleted. Changes in ${diff.deletedSheets.length > 1 ? 'these sheets' : 'this sheet'} cannot be shown.`
        setCompareError(warningMsg)
        console.warn(warningMsg)
      } else {
        setCompareError(null)
      }

      await diffHighlighter.applyHighlights(diff)
      setComparingTurnId(turnId)
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to show changes'
      console.error('Failed to apply diff highlights', error)
      setCompareError(errorMsg)
    } finally {
      setIsComparingLoading(false)
    }
  }

  const handleRevert = (turnId: string) => {
    setPendingRevertTurnId(turnId)
    setRevertToAfter(false)
    setRevertDialogOpen(true)
  }

  const confirmRevert = async () => {
    if (pendingRevertTurnId === null) return

    setRevertDialogOpen(false)
    setIsReverting(true)

    try {
      console.log(`[REVERT] Starting revert for turn ${pendingRevertTurnId}`)

      const turnId = pendingRevertTurnId

      if (comparingTurnId !== null) {
        await diffHighlighter.clearHighlights()
        setComparingTurnId(null)
      }

      // Try delta-based restore first (much faster)
      if (platform === 'excel') {
        const deltaChain = await getDeltaChain(turnId, chatId)
        if (deltaChain?.isComplete && deltaChain.deltas.length > 0) {
          console.log(
            `[REVERT] Attempting delta-based restore (${deltaChain.deltas.length} deltas)`
          )
          const success = await restoreFromDeltaChainExcel(deltaChain)
          if (success) {
            console.log('[REVERT] Delta-based restore succeeded')
            setCurrentSnapshotId(`${turnId}-before`)
            return
          }
          console.log(
            '[REVERT] Delta-based restore failed, falling back to full snapshot'
          )
        }
      }

      // Fallback: full snapshot restore
      const snapshotId = await getSnapshotIdForTurn(turnId, chatId)
      if (!snapshotId) {
        const errorMsg = `No snapshot found for turn ${turnId}`
        console.error(`[REVERT] ${errorMsg}`)
        return
      }

      console.log(`[REVERT] Found snapshot ID: ${snapshotId}`)
      const targetSnapshot = await getSnapshot(snapshotId)
      if (!targetSnapshot) {
        const errorMsg = `Snapshot ${snapshotId} not found or expired`
        console.error(`[REVERT] ${errorMsg}`)
        return
      }

      // Perform revert (this will restore deleted sheets and remove added ones)
      console.log('[REVERT] Restoring workbook snapshot...')
      await restoreWorkbookSnapshot(targetSnapshot)
      console.log('[REVERT] Workbook restored successfully')

      // Update current snapshot state instead of adding message
      setCurrentSnapshotId(`${turnId}-before`)
      console.log('[REVERT] Revert complete')
    } catch (error) {
      console.error('[REVERT] Failed to revert workbook', error)
    } finally {
      setPendingRevertTurnId(null)
      setIsReverting(false)
      setRevertToAfter(false)
    }
  }

  const handleRevertToAfterSnapshot = (turnId: string) => {
    setPendingRevertTurnId(turnId)
    setRevertToAfter(true)
    setRevertDialogOpen(true)
  }

  const confirmRevertToAfter = async () => {
    if (pendingRevertTurnId === null) return

    setRevertDialogOpen(false)
    setIsReverting(true)

    try {
      const turnId = pendingRevertTurnId
      console.log(
        `[REVERT] Starting revert to after snapshot for turn ${turnId}`
      )

      // Use the "after" snapshot for this turn
      const snapshotId = await getAfterSnapshotIdForTurn(turnId, chatId)
      if (!snapshotId) {
        const errorMsg = `No after snapshot found for turn ${turnId}`
        console.error(`[REVERT] ${errorMsg}`)
        return
      }

      console.log(`[REVERT] Found after snapshot ID: ${snapshotId}`)
      const targetSnapshot = await getSnapshot(snapshotId)
      if (!targetSnapshot) {
        const errorMsg = `After snapshot ${snapshotId} not found or expired`
        console.error(`[REVERT] ${errorMsg}`)
        return
      }

      if (comparingTurnId !== null) {
        await diffHighlighter.clearHighlights()
        setComparingTurnId(null)
      }

      // Perform revert
      console.log('[REVERT] Restoring workbook to after snapshot...')
      await restoreWorkbookSnapshot(targetSnapshot)
      console.log('[REVERT] Workbook restored successfully')

      // Update current snapshot state instead of adding message
      setCurrentSnapshotId(`${turnId}-after`)
      console.log('[REVERT] Revert to after snapshot complete')
    } catch (error) {
      console.error('[REVERT] Failed to revert to after snapshot', error)
    } finally {
      setPendingRevertTurnId(null)
      setIsReverting(false)
    }
  }

  // Clear compare mode when sending new message
  useEffect(() => {
    if (loading && comparingTurnId !== null) {
      diffHighlighter.clearHighlights()
      setComparingTurnId(null)
      setCompareError(null)
    }
    // Clear revert state when sending new message
    if (loading) {
      setCurrentSnapshotId(null)
    }
  }, [loading, comparingTurnId, diffHighlighter])

  // Clear compare mode when switching chats
  useEffect(() => {
    void chatId
    // Always clear highlights and reset state when chatId changes
    diffHighlighter.clearHighlights().catch((error) => {
      console.error('Failed to clear highlights on chat switch', error)
    })
    setTurns([])
    setComparingTurnId(null)
    setCurrentSnapshotId(null)
    setCompareError(null)
    setIsComparingLoading(false)
    setIsReverting(false)
    // Reset the attempted load ref when chatId changes
    // This ensures we can reload messages if they become empty during HMR
    attemptedInitialLoadChatIdRef.current = null
    // Reset loading ref to avoid false triggers when switching chats
    previousLoadingRef.current = false
  }, [chatId, diffHighlighter])

  return (
    <div className="flex flex-col h-full">
      <Conversation aria-live="polite">
        <ConversationContent>
          {messages.length === 0 && !isLoadingMessages ? (
            <ConversationEmptyState description="What do you need help with?">
              <div className="flex size-full flex-col items-center justify-center gap-4 p-8 text-center">
                <p className="text-muted-foreground text-sm">
                  What do you need help with?
                </p>
                {onSuggestionClick && (
                  <SuggestionsCrossfade
                    connectors={connectors}
                    sheetSuggestions={sheetSuggestions}
                    onSuggestionClick={onSuggestionClick}
                  />
                )}
              </div>
            </ConversationEmptyState>
          ) : (
            <>
              {(() => {
                // ARCHITECTURE: messages prop is the source of truth for rendering
                // turns are only used for metadata (snapshots, turn boundaries)

                // Build display turns from messages
                // Each turn is a user message + assistant response pair
                const displayTurns: Array<{
                  turnId: string | null
                  messages: ModelMessage[]
                  startIndex: number
                }> = []

                let currentTurnMessages: ModelMessage[] = []
                let currentStartIndex = 0

                // Group messages into turns (user + assistant pairs)
                messages.forEach((msg, idx) => {
                  if (msg.role === 'user') {
                    // Start a new turn
                    if (currentTurnMessages.length > 0) {
                      // Save previous turn
                      displayTurns.push({
                        turnId: findTurnIdForMessageIndex(
                          turns,
                          displayTurns.length
                        ),
                        messages: currentTurnMessages,
                        startIndex: currentStartIndex,
                      })
                    }
                    currentTurnMessages = [msg]
                    currentStartIndex = idx
                  } else {
                    // Add to current turn
                    currentTurnMessages.push(msg)
                  }
                })

                // Don't forget the last turn
                if (currentTurnMessages.length > 0) {
                  displayTurns.push({
                    turnId: findTurnIdForMessageIndex(
                      turns,
                      displayTurns.length
                    ),
                    messages: currentTurnMessages,
                    startIndex: currentStartIndex,
                  })
                }

                // Calculate startIndex for consistent indexing
                let messageIndex = 0
                const turnsWithIndex = displayTurns.map((turn) => {
                  const startIndex = messageIndex
                  messageIndex += turn.messages.length
                  return { ...turn, startIndex }
                })

                return turnsWithIndex.map((turn, turnIndex) => {
                  const turnId = turn.turnId
                  const turnSnapshotInfo = turnId ? snapshotInfo[turnId] : null
                  const hasSnapshot = turnSnapshotInfo?.hasSnapshot ?? false
                  const isComparing = comparingTurnId === turnId

                  return (
                    <div key={turnId || `turn-${turnIndex}`}>
                      {turnId && hasSnapshot && (
                        <TurnSnapshotAction
                          onRevert={() => handleRevert(turnId)}
                          isReverting={isReverting}
                          isComparing={false}
                          isComparingLoading={isComparingLoading}
                          isRevertDisabled={isComparingLoading}
                          isActive={currentSnapshotId === `${turnId}-before`}
                          showOnlyReturn={true}
                        />
                      )}

                      {/* Render all messages in this turn */}
                      {turn.messages.map((message, msgIdx) => {
                        const globalIdx = turn.startIndex + msgIdx
                        const toolParts =
                          'parts' in message && Array.isArray(message.parts)
                            ? (message.parts.filter(
                                (part): part is ToolUIPart =>
                                  typeof part === 'object' &&
                                  part !== null &&
                                  'type' in part &&
                                  typeof part.type === 'string' &&
                                  part.type.startsWith('tool-')
                              ) as ToolUIPart[])
                            : []

                        const fileParts =
                          'parts' in message && Array.isArray(message.parts)
                            ? (message.parts.filter(
                                (part): part is FileUIPart =>
                                  typeof part === 'object' &&
                                  part !== null &&
                                  'type' in part &&
                                  part.type === 'file' &&
                                  'url' in part &&
                                  typeof part.url === 'string' &&
                                  'mediaType' in part &&
                                  typeof part.mediaType === 'string'
                              ) as FileUIPart[])
                            : []

                        const messageText =
                          typeof message.content === 'string'
                            ? message.content
                            : ''
                        const hasTextContent = messageText.length > 0
                        const hasRenderableContent =
                          hasTextContent || fileParts.length > 0

                        return (
                          <div key={`${chatId}-${globalIdx}`}>
                            {toolParts.map((toolPart) => (
                              <Tool
                                key={`${chatId}-${globalIdx}-tool-${toolPart.toolCallId}`}
                                defaultOpen={false}
                              >
                                <ToolHeader
                                  type={toolPart.type}
                                  state={toolPart.state}
                                />
                                <ToolContent>
                                  <ToolInput input={toolPart.input} />
                                  <ToolOutput
                                    output={toolPart.output}
                                    errorText={toolPart.errorText}
                                  />
                                </ToolContent>
                              </Tool>
                            ))}
                            {hasRenderableContent && (
                              <Message
                                from={message.role as 'user' | 'assistant'}
                                className={`${message.role === 'user' ? 'py-2' : ''}`}
                              >
                                <MessageContent>
                                  {hasTextContent && (
                                    <MessageResponse>
                                      {messageText}
                                    </MessageResponse>
                                  )}
                                  {fileParts.length > 0 && (
                                    <MessageAttachments>
                                      {fileParts.map((file) => (
                                        <MessageAttachment
                                          key={`${chatId}-${globalIdx}-file-${file.filename ?? file.url}`}
                                          data={file}
                                        />
                                      ))}
                                    </MessageAttachments>
                                  )}
                                </MessageContent>
                              </Message>
                            )}
                          </div>
                        )
                      })}

                      {turnId &&
                        hasSnapshot &&
                        turnSnapshotInfo?.hasAfterSnapshot && (
                          <TurnSnapshotAction
                            onRevert={() => {
                              handleRevertToAfterSnapshot(turnId)
                            }}
                            onCompareToggle={() => handleCompareToggle(turnId)}
                            isReverting={isReverting}
                            isComparing={isComparing}
                            isComparingLoading={isComparingLoading}
                            isRevertDisabled={isComparingLoading}
                            isActive={currentSnapshotId === `${turnId}-after`}
                          />
                        )}
                    </div>
                  )
                })
              })()}
              {loading && isActive && (
                <Message from="assistant">
                  <MessageContent className="flex flex-row items-center gap-2 pl-1">
                    <GlowingDot size="sm" pulse={true} color="orange" />
                    <Shimmer className="text-xs">
                      {
                        [
                          'Cooking...',
                          'Brewing magic...',
                          'Crunching numbers...',
                          'Working on it...',
                          'Thinking...',
                          'Processing...',
                          'Computing...',
                          'Analyzing...',
                        ][Math.floor(Math.random() * 8)]
                      }
                    </Shimmer>
                  </MessageContent>
                </Message>
              )}
              {compareError && (
                <Message from="assistant">
                  <MessageContent className="text-sm text-destructive">
                    {compareError}
                  </MessageContent>
                </Message>
              )}
            </>
          )}
        </ConversationContent>
        <ConversationScrollButton aria-label="Scroll to latest" />
      </Conversation>

      <Dialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return to Snapshot?</DialogTitle>
            <DialogDescription className="flex flex-col">
              <span>
                This feature is <b>EXPERIMENTAL</b>
              </span>
              <span>Some formatting might be lost</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row gap-2 justify-center">
            <Button
              variant="outline"
              onClick={() => setRevertDialogOpen(false)}
              disabled={isReverting}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={revertToAfter ? confirmRevertToAfter : confirmRevert}
              disabled={isReverting}
              className="text-white"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
